/**
 * Custom Typography wrapper component
 *
 * Enforces typography rules across the entire application:
 * - Headings (h1-h6): Noto Sans, 600 weight, 110% line-height
 * - Body text: Noto Sans, 400 weight, standard line-heights
 * - All text: #001C53 color, 0.25px letter-spacing
 *
 * This component prevents developers from overriding critical typography styles
 * (fontFamily, fontWeight, lineHeight for headings) via sx prop, ensuring
 * design system consistency.
 *
 * Custom body variants:
 * - bodyLarge: 16px / 24px line-height
 * - bodyRegular: 15px / 24px line-height (default)
 * - bodySmall: 13px / 20px line-height
 *
 * @module Typography
 */
import React from 'react';
import { Typography as MuiTypography, TypographyProps as MuiTypographyProps } from '@mui/material';
import { CustomTypographyProps } from './Typography.types';
import { Colors, FontFamily } from '@styles/index';

/**
 * Typography component with enforced design system rules
 *
 * This wrapper ensures consistent typography across the application by:
 * 1. Enforcing Noto Sans for all headings (h1-h6)
 * 2. Enforcing Noto Sans for all body text
 * 3. Preventing font overrides via sx prop for headings
 * 4. Allowing safe overrides (color, spacing) while maintaining font consistency
 *
 * @param props - Typography properties including variant and sx
 * @param props.variant - Typography variant (h1-h6, body1, body2, bodyLarge, bodyRegular, bodySmall, etc.)
 * @param props.sx - Style overrides (fontFamily, fontWeight, lineHeight cannot override heading styles)
 * @returns JSX Element with enforced typography rules
 *
 * @example
 * ```tsx
 * // Heading - automatically uses Libre Baskerville
 * <Typography variant="h1">Main Title</Typography>
 *
 * // Body text - automatically uses Roboto
 * <Typography variant="bodyRegular">Body content</Typography>
 *
 * // Custom body variant
 * <Typography variant="bodyLarge">Large body text</Typography>
 *
 * // Safe overrides (color, spacing allowed)
 * <Typography variant="h2" sx={{ color: 'red', mb: 2 }}>
 *   Colored Heading
 * </Typography>
 *
 * // Font override attempt (will be ignored for headings)
 * <Typography variant="h3" sx={{ fontFamily: 'Arial' }}>
 *   Still uses Noto Sans
 * </Typography>
 * ```
 */
export const Typography: React.FC<CustomTypographyProps> = ({ variant = 'body1', sx, ...rest }) => {
  // Map custom body variants to MUI variants and apply custom styles
  const getMuiVariant = (): MuiTypographyProps['variant'] => {
    switch (variant) {
      case 'bodyLarge':
      case 'bodyRegular':
        return 'body1';
      case 'bodySmall':
        return 'body2';
      default:
        return variant;
    }
  };

  // Apply custom styles for body size variants
  const getCustomStyles = () => {
    // Determine if this is a heading variant
    const isHeading =
      variant?.startsWith('h') || ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(variant || '');

    // Base styles that should ALWAYS be applied
    const baseStyles: any = {
      letterSpacing: '0.25px',
      color: Colors.navy,
    };

    // Enforce font family, weight, and line-height based on variant type
    if (isHeading) {
      baseStyles.fontFamily = FontFamily.primary;
      baseStyles.fontWeight = 600;
      baseStyles.lineHeight = '110%';
      baseStyles.verticalAlign = 'middle';
    } else {
      baseStyles.fontFamily = FontFamily.primary;
      baseStyles.fontWeight = 400;
    }

    switch (variant) {
      case 'bodyLarge':
        return {
          ...baseStyles,
          fontSize: '16px',
          lineHeight: '24px',
        };
      case 'bodyRegular':
        return {
          ...baseStyles,
          fontSize: '15px',
          lineHeight: '24px',
        };
      case 'bodySmall':
        return {
          ...baseStyles,
          fontSize: '13px',
          lineHeight: '20px',
        };
      default:
        // For standard variants, enforce base styles (fontFamily, fontWeight, color, letterSpacing)
        // Theme will handle fontSize and lineHeight, but we ensure critical styles are applied
        return baseStyles;
    }
  };

  // Get base styles that should always be enforced
  const enforcedStyles = getCustomStyles();
  const isHeading =
    variant?.startsWith('h') || ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(variant || '');

  // Merge with sx prop, but ensure critical typography styles can't be overridden
  // This prevents developers from accidentally breaking the design system
  const mergedSx: any = {
    ...enforcedStyles,
    ...sx,
    // CRITICAL: Re-enforce these styles AFTER sx merge to prevent overrides
    // These are design system requirements and must not be changed
    fontFamily: enforcedStyles.fontFamily,
    fontWeight: enforcedStyles.fontWeight,
    color: enforcedStyles.color,
    letterSpacing: enforcedStyles.letterSpacing,
  };

  // For headings, ALWAYS enforce line-height and vertical-align (cannot be overridden)
  // These are critical for heading appearance and consistency
  if (isHeading) {
    mergedSx.lineHeight = '110%';
    mergedSx.verticalAlign = 'middle';
    // Explicitly prevent font family override for headings
    mergedSx.fontFamily = FontFamily.primary;
    mergedSx.fontWeight = 600;
  }

  return <MuiTypography variant={getMuiVariant()} sx={mergedSx} {...rest} />;
};

export default Typography;
